/// <amd-module name="VSSUI/Components/Timeline/Timeline" />
import * as React from "react";
import { Icon } from "VSSUI/Icon";
import { IListItemDetails, List } from "VSSUI/List";
import { css } from "VSSUI/Util";
import { ITimelineProps, ITimelineSequenceProps } from "./Timeline.Props";

/**
 * The timeline is an advanced list that allows the consumer to render a set of
 * items as effectively 2 columns, 1 that shows a linkage between the elements
 * or sequencing, and the second a rich element with details about the item.
 *
 * @param props The ITimelineProps represent the data the functions used to
 *  render the Timeline.
 */
export function Timeline<T>(props: ITimelineProps<T>) {
    const { className, itemProvider, listRef, pageSize } = props;

    const renderRow = (rowIndex: number, item: T, details: IListItemDetails<T>): JSX.Element | null => {
        const [sequenceElement, dataElement] = props.renderItem(rowIndex, item, details);

        return (
            <tr
                className={css(
                    "bolt-timeline-row",
                    rowIndex === 0 && "bolt-timeline-first-row",
                    rowIndex === props.itemProvider.length - 1 && "bolt-timeline-last-row"
                )}
                data-row-index={rowIndex}
                role="presentation"
            >
                <td className="bolt-timeline-cell flex-row" data-column-index={0} role="presentation">
                    {sequenceElement}
                    <div className="bolt-timeline-cell-content flex-row flex-grow scroll-hidden">{dataElement}</div>
                </td>
            </tr>
        );
    };

    return (
        <List<T>
            className={className}
            itemProvider={itemProvider}
            pageSize={pageSize}
            ref={listRef}
            rowHeights={props.rowHeights}
            renderRow={renderRow}
            role="presentation"
            width="100%"
        />
    );
}

/**
 * The TimelineSequence is a good default timeline sequence UI. It can be used
 * to show an icon and a vertical line connecting each element. Note it is the
 * responsibiliy of the consumer to ensure the sequence elements all have the
 * same width for the visualization to work properly.
 *
 * @param props The ITimelineSequenceProps that represent this elements sequence
 *  in the timeline.
 */
export function TimelineSequence(props: ITimelineSequenceProps): JSX.Element {
    return (
        <div className={css(props.className, "bolt-timeline-sequence flex-column flex-center flex-noshrink")}>
            <div className={css("bolt-timeline-icon-prefix", props.iconCentered && "flex-grow", props.hidePrefix && "invisible")} />
            <Icon {...props.iconProps} className={css("bolt-timeline-icon", props.iconProps.className)} />
            <div className={css("bolt-timeline-icon-suffix flex-grow", props.hideSuffix && "invisible")} />
        </div>
    );
}
